import { Box, Grid, GridItem, Skeleton, useToken } from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FcFolder } from 'react-icons/fc';
import { GrDownload } from 'react-icons/gr';
import { IoEyeOutline } from 'react-icons/io5';
import { MdAdd, MdDownload } from 'react-icons/md';
import { useSearchParams } from 'react-router';
import { ChevronLeftIcon } from '@/assets/icons';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import { FaceliftTag } from '@/components/Atoms/FaceliftTag';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import Show from '@/components/Molecules/Show';
import DataTable from '@/components/Organisms/DataTable';
import type {
	ColumnData,
	onFilterApplyProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import { UploadDocuments } from '@/components/Templates/BenefitVerification/Steps';
import { colors } from '@/components/ui/theme';
import type { UploadedFile } from '@/hooks';
import {
	useGetSharePointFileContent,
	useGetSharePointList,
} from '@/hooks/service-hooks/common';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import { getSharePointFileContent } from '@/services/common';
import { useClinicStore } from '@/store';
import type {
	SharePointFileItem,
	SharePointFolderItem,
} from '@/types/sharepoint';
import type { FilterType } from '@/types/table';
import { buildClinicKey } from '@/utils/navigationItems';
import { documentColumns, fileTypeFilterOptions } from './documents';

const DocumentsTable = () => {
	const [brand600] = useToken('colors', ['brand.600']);

	const [fileType, setFileType] = useState<SelectItem>({
		label: 'All',
		value: '',
	});
	const { defaultClinicIds, mapGlobalFilters } = useClinicFilter(
		'_parentcustomerid_value',
	);
	const [searchParams, setSearchParams] = useSearchParams();
	const [viewState, setViewState] = useState<{
		selectedClinic: SelectItem | null;
		folderName: string;
		fileName: string;
	}>({
		selectedClinic: null,
		folderName: '',
		fileName: '',
	});

	const { folderName, fileName } = viewState;

	const currentClinics = useClinicStore((state) => state.currentClinics);
	const clinicSelectItems: SelectItem[] = useMemo(() => {
		return currentClinics.map((clinic) => ({
			label: clinic.name,
			value: clinic.accountid,
		}));
	}, [currentClinics]);

	// Active clinic that triggers API calls (set when "Select" button is clicked)
	const [activeClinic, setActiveClinic] = useState<SelectItem | null>(null);

	const updateSearchParams = useCallback(
		(
			nextClinic?: SelectItem | null,
			nextFolder?: string,
			nextFile?: string,
		) => {
			setSearchParams((prev) => {
				const params = new URLSearchParams(prev);

				const clinicKey = buildClinicKey(nextClinic);
				if (clinicKey) {
					params.set('clinic', clinicKey);
				} else {
					params.delete('clinic');
				}

				if (nextFolder) {
					params.set('folder', nextFolder);
				} else {
					params.delete('folder');
				}

				if (nextFile) {
					params.set('file', nextFile);
				} else {
					params.delete('file');
				}

				return params;
			});
		},
		[setSearchParams, buildClinicKey],
	);

	// Initialize / sync clinic & folder from query params
	useEffect(() => {
		if (!currentClinics.length) return;

		const clinicFromQuery = searchParams.get('clinic');
		const folderFromQuery = searchParams.get('folder') || '';
		const fileFromQuery = searchParams.get('file') || '';

		const clinicFromUrl =
			clinicSelectItems.find((c) => clinicFromQuery === buildClinicKey(c)) ||
			null;

		const initialClinic = clinicFromUrl || null;

		setViewState((prev) => ({
			...prev,
			selectedClinic: initialClinic,
			folderName: folderFromQuery,
			fileName: fileFromQuery,
		}));

		// Set active clinic from URL params (this triggers API call on initial load)
		setActiveClinic(initialClinic);

		// If URL is missing or out of sync, normalize it
		if (initialClinic && clinicFromQuery !== buildClinicKey(initialClinic)) {
			updateSearchParams(initialClinic, folderFromQuery, fileFromQuery);
		}
	}, [
		currentClinics,
		clinicSelectItems,
		searchParams,
		updateSearchParams,
		buildClinicKey,
	]);

	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: defaultClinicIds.length > 0,
		status: '',
		globalFilters: JSON.stringify(
			defaultClinicIds.length
				? [
						{
							key: '_parentcustomerid_value',
							values: defaultClinicIds,
						},
					]
				: [],
		),
		sort: '',
	});

	// Fetch SharePoint list - pass folderName as query param
	const { data: sharePointData, isFetching: isLoading } = useGetSharePointList({
		clinic: buildClinicKey(activeClinic).replaceAll('&', 'and'),
		folder: folderName,
		sort: filters.sort || '',
		columnsFilters: filters.columnsFilters,
		fileType: (fileType?.value as string) || '',
		enabled: !!activeClinic?.value,
	});

	// Modal state management
	const [modalInformation, setModalInformation] = useState<{
		mode: 'view' | 'upload' | null;
		data: string | null;
	}>({
		mode: null,
		data: null,
	});
	//
	// Handle filter apply
	const onFilterApply = ({
		page,
		limit,
		status,
		search = '',
		sort = null,
		columnsFilters = [],
		globalFilters = [],
	}: onFilterApplyProps) => {
		const mappedFilters = mapGlobalFilters(globalFilters || []);

		setFilters({
			page,
			limit,
			columnsFilters: JSON.stringify(columnsFilters),
			search,
			enabled: defaultClinicIds.length > 0,
			status: status?.value,
			globalFilters: JSON.stringify(mappedFilters),
			sort: sort ? `${sort.key}|${sort.direction}` : '',
		});
	};

	// Handle selection
	const onSelection = (ids: string[]) => {
		// Handle selection logic here if needed
	};

	const handleCloseModal = () => {
		setModalInformation({ mode: null, data: null });
		// Clear file parameter when modal is closed
		if (fileName) {
			lastOpenedFileRef.current = ''; // Reset ref to allow reopening if needed
			setViewState((prev) => ({
				...prev,
				fileName: '',
			}));
			updateSearchParams(activeClinic, folderName, '');
		}
	};

	const handleOpenDocs = useCallback(
		(clickedFolderName: string) => {
			// Append folder name to the current path
			const newFolder =
				folderName === ''
					? clickedFolderName
					: `${folderName}/${clickedFolderName}`;
			setViewState((prev) => ({
				...prev,
				folderName: newFolder,
				fileName: '', // Clear file when navigating to a folder
			}));
			updateSearchParams(activeClinic, newFolder, '');
		},
		[folderName, updateSearchParams, activeClinic],
	);

	const handleGoBack = useCallback(() => {
		// Remove one level from the path
		let newFolder = '';
		if (folderName.includes('/')) {
			const parts = folderName.split('/');
			parts.pop(); // Remove last part
			newFolder = parts.join('/');
		} else {
			// If no "/" separator, we're at the first level, go back to root
			newFolder = '';
		}
		setViewState((prev) => ({
			...prev,
			folderName: newFolder,
			fileName: '', // Clear file when going back
		}));
		updateSearchParams(activeClinic, newFolder, '');
	}, [folderName, updateSearchParams, activeClinic]);

	const handleDownloadDocument = useCallback(
		async (item: ColumnData) => {
			const fileName = (item.fileName as string) || 'document';

			// Prefer activeClinic; if not set, fall back to selected clinic
			const clinicForDownload = activeClinic || viewState.selectedClinic;
			if (!clinicForDownload) return;

			const clinicKey = buildClinicKey(clinicForDownload);

			try {
				const file = await getSharePointFileContent(
					clinicKey,
					folderName,
					fileName,
				);

				// Convert nested byte array to Blob (similar to IVRViewModal)
				const byteArray = new Uint8Array(file.data.data);
				const blob = new Blob([byteArray], { type: file.mimeType });
				const url = window.URL.createObjectURL(blob);

				const link = document.createElement('a');
				link.href = url;
				link.download = fileName;
				link.target = '_blank';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				window.URL.revokeObjectURL(url);
			} catch {
				// Silently fail for now; could add toast notification later
			}
		},
		[activeClinic, viewState.selectedClinic, folderName, buildClinicKey],
	);

	const handleDownload = () => {
			if (fileViewUrl) {
				const link = document.createElement('a');
				link.href = fileViewUrl;
				link.download = viewState.fileName;
				link.target = '_blank';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
	};

	const documentsData: ColumnData[] = useMemo(() => {
		if (!sharePointData?.data) return [];

		const folders: ColumnData[] = (sharePointData.data.folders || []).map(
			(item: SharePointFolderItem) => ({
				id: item.folderPath || item.folderName,
				folderName: item.folderName,
				name: (
					<Box
						cursor="pointer"
						display="flex"
						alignItems="center"
						gap={2}
						onClick={() => handleOpenDocs(item.folderName)}
					>
						<FcFolder size={24} />
						<FaceliftTruncatedTextWithTooltip
							maxLength={40}
							text={item.folderName}
						/>
					</Box>
				),
				type: 'Folder',
				modifiedOn: dayjs(item.modifiedOn)
					.tz('America/New_York')
					.format('DD/MM/YYYY'),
			}),
		);

		const files: ColumnData[] = (sharePointData.data.files || []).map(
			(item: SharePointFileItem) => ({
				id: item.fileUrl,
				fileName: item.fileName,
				name: (
					<FaceliftTruncatedTextWithTooltip
						text={item.fileName || '-'}
						maxLength={90}
					/>
				),
				type: item.fileName?.split('.').pop()?.toUpperCase() || 'File',
				modifiedOn: dayjs(item.modifiedOn)
					.tz('America/New_York')
					.format('DD/MM/YYYY'),
				url: item.fileUrl,
			}),
		);

		return [...folders, ...files];
	}, [sharePointData, folderName, handleOpenDocs]);

	// Track the last fileName we tried to open to prevent duplicate attempts
	const lastOpenedFileRef = useRef<string>('');

	// Reset ref when fileName changes to a different value
	useEffect(() => {
		if (lastOpenedFileRef.current && lastOpenedFileRef.current !== fileName) {
			lastOpenedFileRef.current = '';
		}
	}, [fileName]);

	// Auto-open modal when file parameter is set in URL
	useEffect(() => {
		// Skip if no fileName, no data, or still loading
		if (!fileName || !documentsData.length || isLoading) {
			return;
		}

		// Check if modal is already showing this file
		if (modalInformation.mode === 'view' && modalInformation.data) {
			const currentFile = documentsData.find(
				(item) => item.id === modalInformation.data && item.type !== 'Folder',
			);
			if (
				currentFile &&
				(currentFile.fileName as string)?.trim() === fileName.trim()
			) {
				// Modal is already showing the correct file, no need to reopen
				return;
			}
		}

		// Skip if we already tried to open this file
		if (lastOpenedFileRef.current === fileName) {
			return;
		}

		// Normalize fileName for comparison (trim - searchParams.get() already decodes)
		const normalizedFileName = fileName.trim();

		// Find the file in documentsData by matching fileName
		let fileItem = documentsData.find((item) => {
			if (item.type === 'Folder') return false;
			const itemFileName = (item.fileName as string)?.trim() || '';
			// Try exact match first, then case-insensitive
			return (
				itemFileName === normalizedFileName ||
				itemFileName.toLowerCase() === normalizedFileName.toLowerCase()
			);
		});

		// If not found by fileName, try matching by URL (extract filename from URL)
		if (!fileItem) {
			fileItem = documentsData.find((item) => {
				if (item.type === 'Folder' || !item.url) return false;
				const url = item.url as string;
				// Extract filename from URL (last segment after /)
				const urlFileName = url?.split('/').pop()?.split('?')[0] || ''; // Remove query params if any
				// Try to decode if URL-encoded, but also try without decoding
				try {
					const decodedUrlFileName = decodeURIComponent(urlFileName);
					if (
						decodedUrlFileName === normalizedFileName ||
						decodedUrlFileName.toLowerCase() ===
							normalizedFileName.toLowerCase()
					) {
						return true;
					}
				} catch {
					// If decode fails, continue with original
				}
				return (
					urlFileName === normalizedFileName ||
					urlFileName.toLowerCase() === normalizedFileName.toLowerCase()
				);
			});
		}

		// Open modal if file is found
		if (fileItem && fileItem.id) {
			lastOpenedFileRef.current = fileName;
			setModalInformation({ mode: 'view', data: fileItem.id as string });
		}
	}, [fileName, documentsData, isLoading, modalInformation]);

	const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
	const [fileViewUrl, setFileViewUrl] = useState<string>('');

	// Prefer activeClinic; fall back to selected clinic (same as download)
	const clinicForView = activeClinic || viewState.selectedClinic;
	const clinicKeyForView = clinicForView ? buildClinicKey(clinicForView) : '';

	const { data: sharePointFile, isLoading: isFileLoading } =
		useGetSharePointFileContent({
			clinic: clinicKeyForView.replaceAll('&', 'and'),
			folderName,
			fileName: viewState.fileName,
			enabled: !!clinicKeyForView && !!viewState.fileName,
		});

	useEffect(() => {
		// Cleanup and reset when there is no file data
		if (!sharePointFile) {
			if (fileViewUrl) {
				window.URL.revokeObjectURL(fileViewUrl);
			}
			setFileViewUrl('');
			return;
		}

		const byteArray = new Uint8Array(sharePointFile.data.data);
		const blob = new Blob([byteArray], { type: sharePointFile.mimeType });
		const objectUrl = window.URL.createObjectURL(blob);

		// Revoke previous URL if any
		if (fileViewUrl) {
			window.URL.revokeObjectURL(fileViewUrl);
		}

		setFileViewUrl(objectUrl);

		return () => {
			window.URL.revokeObjectURL(objectUrl);
		};
	}, [sharePointFile]);

	const isSupportedFileType = useMemo(() => {
		const fileExtension = viewState.fileName
			?.split('.')
			.pop()
			?.toUpperCase();
		const supportedTypes = ['PDF', 'JPG', 'PNG', 'JPEG'];
		return !!(fileExtension && supportedTypes.includes(fileExtension));
	}, [viewState.fileName]);

	return (
		<>
			<Box
				p={{ base: 4, md: 8 }}
				py={{ base: 4, md: 6 }}
				bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<FaceliftText fontWeight="bold">Documents</FaceliftText>
					<Box display="flex" gap={4}>
						<Box width={150}>
							<FaceliftSelect<SelectItem, false>
								items={fileTypeFilterOptions}
								isSearchable={false}
								value={fileType}
								onChange={(newValue) => setFileType(newValue as SelectItem)}
							/>
						</Box>
						<Show when={folderName === 'Client Inbox'}>
							<FaceliftIconButton
								title="Add"
								onClick={() =>
									setModalInformation({ mode: 'upload', data: null })
								}
							>
								<MdAdd />
							</FaceliftIconButton>
						</Show>
					</Box>
				</Box>
				<Grid
					gap={2}
					templateColumns={{
						base: '1fr',
						sm: 'repeat(5, 1fr)',
						md: 'repeat(7, 1fr)',
						lg: 'repeat(7, 1fr)',
						xl: 'repeat(6, 1fr)',
					}}
				>
					<GridItem
						colSpan={{ base: 1, sm: 2, md: 2, lg: 2, xl: 1 }}
						mt={4}
						borderRadius="xl"
						p={4}
						bg={{ base: 'white', _dark: colors.tabelCellDark }}
						display="flex"
						flexDirection="column"
						justifyContent="flex-start"
					>
						<Show when={folderName !== ''}>
							<Box
								display="flex"
								alignItems="center"
								justifyContent="flex-start"
								gap={2}
								onClick={handleGoBack}
								cursor="pointer"
								mb={4}
							>
								<Box
									bg="linear-gradient(90deg, {colors.brand.solid} 0%, {colors.brand.solidSecondary} 100%)"
									color="white"
									padding={2}
									borderRadius="full"
									cursor="pointer"
								>
									<ChevronLeftIcon width={10} height={10} />
								</Box>
								<FaceliftText fontWeight={600} fontSize="md">
									Go Back
								</FaceliftText>
							</Box>
						</Show>
						<Box w="100%" display="flex" flexDirection="column" gap={4}>
							<FaceliftSelect<SelectItem, false>
								items={clinicSelectItems}
								placeholder="Select Clinic"
								value={viewState.selectedClinic}
								onChange={(newValue: SingleValue<SelectItem>) => {
									const selectedItem = newValue as SelectItem | null;
									setViewState((prev) => ({
										...prev,
										selectedClinic: selectedItem,
										folderName: '',
										fileName: '',
									}));
								}}
							/>
							<FaceliftButton
								onClick={() => {
									setActiveClinic(viewState.selectedClinic);
									setViewState((prev) => ({
										...prev,
										folderName: '',
										fileName: '',
									}));
									updateSearchParams(viewState.selectedClinic, '', '');
								}}
								disabled={!viewState.selectedClinic}
							>
								Select
							</FaceliftButton>
						</Box>
					</GridItem>
					<GridItem
						colSpan={{ base: 1, sm: 3, md: 5, lg: 5, xl: 5 }}
						overflowY="auto"
					>
						<DataTable
							tableName="Documents"
							loading={isLoading}
							onSelection={onSelection}
							items={documentsData}
							columns={documentColumns}
							topLabel=""
							totalCount={documentsData.length}
							onFilterApply={onFilterApply}
							bordered
							actions={[
								{
									label: 'Download',
									icon: <GrDownload />,
									actionFn: (item: ColumnData) => handleDownloadDocument(item),
									show: (item: ColumnData) => item.type !== 'Folder',
								},
								{
									label: 'View',
									icon: <IoEyeOutline />,
									actionFn: (item: ColumnData) => {
										// Only show view for files, not folders
										if (item.type !== 'Folder') {
											const file = (item.fileName as string) || '';
											setModalInformation({
												mode: 'view',
												data: item.id as string,
											});
											// Update query params to include the file name
											setViewState((prev) => ({
												...prev,
												fileName: file,
											}));
											updateSearchParams(activeClinic, folderName, file);
										} else {
											handleOpenDocs((item.folderName as string) || '');
										}
									},
									show: (item: ColumnData) => {
										const type = item.type as string;
										return ['Folder', 'PDF', 'JPG', 'PNG', 'JPEG'].includes(
											type,
										);
									},
								},
							]}
						/>
					</GridItem>
				</Grid>
			</Box>

			<FaceliftDialog
				showBackBtn={true}
				title="Document Details"
				open={modalInformation.mode === 'view'}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				sx={{
					width: 900,
					maxWidth: '95vw',
					minHeight: '700',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflowX: 'visible',
					overflowY: 'auto',
				}}
			>
				<Show when={isFileLoading}>
					<Box>
						<Skeleton height="40px" mb={4} />
						<Skeleton height={600} />
					</Box>
				</Show>

				<Show when={!isFileLoading && !fileViewUrl}>
					<FaceliftText>No file selected.</FaceliftText>
				</Show>
				<Show when={!isFileLoading && !!fileViewUrl}>
					<Box>
						<Box
							display="flex"
							justifyContent="flex-end"
							alignItems="center"
							mb={4}
						>
							<FaceliftButton
								onClick={handleDownload}
								size="sm"
								display="flex"
								alignItems="center"
								gap={2}
							>
								<MdDownload />
								Download
							</FaceliftButton>
						</Box>

						<Show when={isSupportedFileType}>
							<Box
								border="1px solid"
								borderColor="gray.200"
								borderRadius="md"
								overflow="hidden"
								height="600px"
							>
								<iframe
									src={`${fileViewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
									width="100%"
									height="100%"
									title="IVR Document"
									style={{ border: 'none' }}
								/>
							</Box>
						</Show>
						<Show when={!isSupportedFileType}>
							<FaceliftText fontSize="lg" fontWeight="medium" p={4}>
								{viewState.fileName}
							</FaceliftText>
						</Show>
					</Box>
				</Show>
			</FaceliftDialog>
			<FaceliftDialog
				showBackBtn={true}
				title="Upload"
				open={modalInformation.mode === 'upload'}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				size="md"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
				}}
			>
				<UploadDocuments
					data={{
						uploadedFiles,
					}}
					onChange={(_, value) => {
						setUploadedFiles(value.uploadedFiles);
					}}
					clinicId={`${activeClinic?.label}_${activeClinic?.value}`}
					clinicName={activeClinic?.label || ''}
				/>
			</FaceliftDialog>
		</>
	);
};

export default DocumentsTable;
